import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

// F2B · Forced first-login password change screen. Reached only when
// `user.must_change_password === true`. Reuses POST /auth/change-password.
export default function SetPasswordPage() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New password fields do not match.");
      return;
    }
    if (next.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (next === current) {
      setError("New password must differ from the temporary password.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      // Refresh /auth/me so the guard clears and normal routes admit.
      const me = await api.get("/auth/me").then((r) => r.data);
      setUser(me);
      nav("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Password change failed.");
    } finally {
      setLoading(false);
    }
  };

  // Any anonymous / already-updated user should not linger here.
  if (user === false) {
    nav("/login");
    return null;
  }
  if (user && user.must_change_password === false) {
    nav("/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen aa-surface flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo surface="light" height={56} withOpsMarker={false} />
          <div className="mt-3 text-[13px] text-steel tracking-wide">Create Your Password</div>
        </div>
        <div className="bg-white border border-coach-silver shadow-sm rounded-md overflow-hidden"
             data-testid="set-password-card">
          <div className="bg-carbon text-white px-6 py-3 text-[12px] tracking-widest uppercase">
            One-Time Password Change
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="text-[12px] text-steel">
              Signed in as <span className="font-mono text-carbon">{user?.email || "…"}</span>.
              A permanent password is required before you can continue.
            </div>
            <PwInput label="Current temporary password" value={current} onChange={setCurrent}
                     show={showPw} setShow={setShowPw} testid="set-pw-current" />
            <PwInput label="New password" value={next} onChange={setNext}
                     show={showPw} setShow={setShowPw} testid="set-pw-new" />
            <PwInput label="Confirm new password" value={confirm} onChange={setConfirm}
                     show={showPw} setShow={setShowPw} testid="set-pw-confirm" />
            {error && (
              <div data-testid="set-pw-error"
                   className="text-[12px] text-critical-red border border-critical-red/40 bg-critical-red/5 rounded-sm px-2.5 py-1.5">
                {error}
              </div>
            )}
            <button data-testid="set-pw-submit" disabled={loading}
                    className="w-full h-10 rounded-sm text-[13px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-60">
              {loading ? "Saving…" : "Set Password"}
            </button>
          </form>
        </div>
        <div className="text-[11px] text-steel mt-3 text-center">
          At least 8 characters. Must differ from the temporary password.
        </div>
      </div>
    </div>
  );
}

const PwInput = ({ label, value, onChange, show, setShow, testid }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-steel mb-1">{label}</div>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required
        className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 pr-16 text-[13px]"
      />
      <button type="button" onClick={() => setShow((s) => !s)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[11px] text-coach-blue px-1">
        {show ? "Hide" : "Show"}
      </button>
    </div>
  </label>
);
